// The All / ASHP / Solar switch on Dashboard and Analytics. It filters every
// figure on the page to one product's components (the stats API's ?product=).
//
// Shown only when the company's subscription (Stripe, via GET /v1/billing)
// entitles it to both products: heat pump estimates (LeadFlow ASHP or
// QuoteFlow, or the old per-component lines) and LeadFlow Solar. With one
// product, or none, there's nothing to switch between, so the page shows "all".
// Our demo company is entitled to everything.
import { useEffect, useState } from "react";
import { LayoutGrid } from "lucide-react";
import type { BillingOverview, Widget } from "../api";
import { ProductIcon, productOf } from "./ProductIcon";

/** What the pages filter by: everything, or one product (contact forms are in "all" only). */
export type ProductFilter = "all" | "ashp" | "solar";

const OPTIONS: { value: ProductFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "ashp", label: "ASHP" },
  { value: "solar", label: "Solar" },
];

/** Is the company entitled to heat pump estimates and to solar estimates? (null = still loading) */
export function entitlements(billing: BillingOverview | null) {
  if (!billing) return { ashp: false, solar: false };
  if (billing.demo) return { ashp: true, solar: true };
  // The API's live slots per category come from the Stripe subscription
  return { ashp: (billing.limits.estimate ?? 0) > 0, solar: (billing.limits.solar ?? 0) > 0 };
}

/**
 * The switch's state for a page: whether to offer it (entitled to both
 * products) and the chosen filter, which falls back to "all" if the switch goes away.
 */
export function useProductFilter(billing: BillingOverview | null) {
  const { ashp, solar } = entitlements(billing);
  const available = ashp && solar;
  const [product, setProduct] = useState<ProductFilter>("all");
  useEffect(() => {
    if (!available) setProduct("all");
  }, [available]);
  return { available, product: available ? product : "all", setProduct };
}

/** The API's product parameter for a filter ("all" = no filter). */
export const productParam = (p: ProductFilter) => (p === "all" ? undefined : p);

/** Does a component belong to the chosen product? */
export const inProduct = (w: Widget, p: ProductFilter) => p === "all" || productOf(w) === p;

export function ProductSwitch({ value, onChange }: { value: ProductFilter; onChange: (p: ProductFilter) => void }) {
  return (
    <div className="ed-segmented product-switch" role="group" aria-label="Product">
      {OPTIONS.map((o) => (
        <button key={o.value} type="button" className={o.value === value ? "is-on" : ""} aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.value === "all" ? (
            // "All": a neutral tile the same size as the product tiles
            <span className="product-icon product-icon-all" style={{ width: 20, height: 20 }} aria-hidden="true">
              <LayoutGrid size={11} aria-hidden="true" />
            </span>
          ) : (
            <ProductIcon product={o.value} size={20} />
          )}
          {o.label}
        </button>
      ))}
    </div>
  );
}
